import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { EvaluationTemplate } from '@/features/webinar/types';
import { Star } from 'lucide-react';
import { useWebinarT } from '@/hooks/useWebinarT';

export default function EvaluationFormPreview({
  visualIdentity,
  evaluation,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  evaluation: EvaluationTemplate;
}) {
  const { t } = useWebinarT('webinars');
  const sortedQuestions = [...evaluation.questions].sort((a, b) => a.position - b.position);

  const renderQuestionContent = (question: (typeof evaluation.questions)[0]) => {
    switch (question.type) {
      case 'SMILEYS':
        return (
          <div className="flex items-center gap-2">
            {['😫', '🙁', '🙂', '😁', '🥳'].map((emoji, index) => (
              <div key={index} className="text-2xl" style={{ opacity: 0.5 }}>
                {emoji}
              </div>
            ))}
          </div>
        );
      case 'THUMBS':
        return (
          <div className="flex items-center gap-2">
            <div className="text-2xl opacity-50">👎</div>
            <div className="text-2xl opacity-50">👍</div>
          </div>
        );
      case 'STARS':
        return (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-6 h-6 ${
                  visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
                }`}
              />
            ))}
          </div>
        );
      case 'TEXT_AREA':
        return (
          <textarea
            placeholder={t('thankyouPage.evaluation.textAreaPlaceholder')}
            className="w-full h-20 px-4 py-2 border text-base border-input rounded-lg resize-none focus:outline-none"
            disabled
            style={{
              backgroundColor:
                visualIdentity?.theme === 'DARK'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(31, 31, 31, 0.03)',
              color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
            }}
          />
        );
      case 'SINGLE_CHOICE':
      case 'MULTIPLE_CHOICE':
        return (
          <div className="space-y-2">
            {(question.options ?? []).map((option) => (
              <div
                key={option.id}
                className={`flex items-center gap-3 rounded-lg border border-input p-3 ${
                  visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
                }`}
              >
                <span
                  className={`size-5 shrink-0 border ${
                    question.type === 'SINGLE_CHOICE' ? 'rounded-full' : 'rounded-[4px]'
                  }`}
                  style={{ borderColor: visualIdentity?.contrastColor || '#2C6CFF' }}
                />
                {option.textContent}
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <Accordion type="single" collapsible className="space-y-4">
        {sortedQuestions.map((question) => (
          <AccordionItem
            key={question.id}
            value={`item-${question.id}`}
            theme={visualIdentity?.theme === 'DARK' ? 'DARK' : 'LIGHT'}
          >
            <AccordionTrigger
              className={`${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
              isChevron
              chevronColor={visualIdentity?.contrastColor || '#2C6CFF'}
            >
              {question.text}
            </AccordionTrigger>
            <AccordionContent>{renderQuestionContent(question)}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
